import { useState, useEffect, useCallback, useRef } from 'react';
import { useBranding } from '@/contexts/BrandingContext';
import { BrandMark } from '@/components/BrandLogo';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { useAuth, api } from '@/App';
import { Button } from '@/components/ui/button';
import { 
  CheckCircle,
  XCircle,
  Loader2,
  Home,
  ArrowLeft
} from 'lucide-react';

export default function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { branding: brandData } = useBranding();
  const [status, setStatus] = useState('loading');
  const [paymentData, setPaymentData] = useState(null);
  const [pollCount, setPollCount] = useState(0);

  const sessionId = searchParams.get('session_id');
  const mpRequestId = searchParams.get('mp_request_id');
  const mpStatus = searchParams.get('mp_status');
  const bancardRequestId = searchParams.get('bancard_request_id');
  const bancardStatus = searchParams.get('bancard_status');

  const pollCountRef = useRef(0);

  const pollPaymentStatus = useCallback(async () => {
    if (pollCountRef.current >= 5) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await api.get(`/payments/status/${sessionId}`);
      setPaymentData(response.data);

      if (response.data.payment_status === 'paid') {
        setStatus('success');
      } else if (response.data.status === 'expired') {
        setStatus('expired');
      } else {
        pollCountRef.current += 1;
        setPollCount(pollCountRef.current);
        setTimeout(pollPaymentStatus, 2000);
      }
    } catch (_) {
      setStatus('error');
    }
  }, [sessionId]);

  const pollMPStatus = useCallback(async () => {
    if (pollCountRef.current >= 8) {
      setStatus('timeout');
      return;
    }
    try {
      const response = await api.get(`/payments/mp/status/${mpRequestId}`);
      setPaymentData(response.data);
      const ps = response.data.payment_status;
      if (ps === 'paid' || ps === 'approved') {
        setStatus('success');
      } else if (ps === 'rejected') {
        setStatus('failed');
      } else {
        pollCountRef.current += 1;
        setPollCount(pollCountRef.current);
        setTimeout(pollMPStatus, 3000);
      }
    } catch (_) {
      setStatus('error');
    }
  }, [mpRequestId]);

  const pollBancardStatus = useCallback(async () => {
    if (pollCountRef.current >= 8) {
      setStatus('timeout');
      return;
    }
    try {
      const response = await api.get(`/payments/bancard/status/${bancardRequestId}`);
      setPaymentData(response.data);
      const ps = response.data.payment_status;
      if (ps === 'paid') {
        setStatus('success');
      } else if (ps === 'failed') {
        setStatus('failed');
      } else {
        pollCountRef.current += 1;
        setPollCount(pollCountRef.current);
        setTimeout(pollBancardStatus, 3000);
      }
    } catch (_) {
      setStatus('error');
    }
  }, [bancardRequestId]);

  useEffect(() => {
    if (bancardRequestId) {
      if (bancardStatus === 'success') {
        // Poll to confirm the webhook was processed
        pollBancardStatus();
      } else {
        setStatus('failed');
      }
    } else if (mpRequestId) {
      // MercadoPago return
      if (mpStatus === 'approved') {
        setStatus('success');
        api.get(`/payments/mp/status/${mpRequestId}`).then(r => setPaymentData(r.data)).catch(() => {});
      } else if (mpStatus === 'rejected') {
        setStatus('failed');
      } else {
        // Pending - poll
        pollMPStatus();
      }
    } else if (sessionId) {
      pollPaymentStatus();
    }
  }, [sessionId, mpRequestId, mpStatus, bancardRequestId, bancardStatus, pollPaymentStatus, pollMPStatus, pollBancardStatus]);

  return (
    <div className="min-h-screen bg-[#F5F4F0]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center space-x-2">
              <BrandMark onDark={false} className="w-10 h-10" alt={brandData?.site_name || 'Estadía 365'} />
              <span className="font-bold text-xl text-[#1C1917]" style={{ fontFamily: 'Outfit' }}>
                Estadia 365
              </span>
            </Link>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="pt-24 pb-16 flex items-center justify-center min-h-screen">
        <div className="max-w-md mx-auto px-4">
          <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
            {status === 'loading' && (
              <>
                <Loader2 className="w-16 h-16 text-[#264639] mx-auto mb-6 animate-spin" />
                <h1 className="text-2xl font-bold text-[#1C1917] mb-2" style={{ fontFamily: 'Outfit' }}>
                  Verificando pago...
                </h1>
                <p className="text-[#57534E]">
                  Por favor espera mientras confirmamos tu pago.
                </p>
              </>
            )}

            {status === 'success' && (
              <>
                <div className="w-20 h-20 bg-[#DCE9E2] rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-12 h-12 text-[#264639]" />
                </div>
                <h1 className="text-2xl font-bold text-[#1C1917] mb-2" style={{ fontFamily: 'Outfit' }}>
                  ¡Pago exitoso!
                </h1>
                <p className="text-[#57534E] mb-6">
                  Tu pago ha sido procesado correctamente. Te hemos enviado un correo con los detalles de tu reserva.
                </p>
                {paymentData && (
                  <div className="bg-[#F5F4F0] rounded-lg p-4 mb-6 text-left">
                    <div className="flex justify-between mb-2">
                      <span className="text-[#78716C]">Monto pagado</span>
                      <span className="font-semibold text-[#1C1917]">
                        {mpRequestId 
                          ? `${paymentData.amount?.toLocaleString()} ${paymentData.currency?.toUpperCase() || 'ARS'}`
                          : bancardRequestId
                            ? `Gs. ${Number(paymentData.amount || 0).toLocaleString('es-PY', { maximumFractionDigits: 0 })}`
                            : `$${(paymentData.amount_total / 100).toFixed(2)} ${paymentData.currency?.toUpperCase()}`
                        }
                      </span>
                    </div>
                    {(paymentData.metadata?.property_title) && (
                      <div className="flex justify-between mb-2">
                        <span className="text-[#78716C]">Propiedad</span>
                        <span className="font-medium text-[#1C1917] text-right text-sm">
                          {paymentData.metadata.property_title.substring(0, 30)}...
                        </span>
                      </div>
                    )}
                    {(paymentData.metadata?.nights) && (
                      <div className="flex justify-between mb-2">
                        <span className="text-[#78716C]">Noches</span>
                        <span className="font-medium text-[#1C1917]">
                          {paymentData.metadata.nights}
                        </span>
                      </div>
                    )}
                    {(mpRequestId || bancardRequestId) && (
                      <div className="flex justify-between">
                        <span className="text-[#78716C]">Método</span>
                        <span className={`font-medium ${mpRequestId ? 'text-[#009EE3]' : 'text-[#E31837]'}`}>{mpRequestId ? 'Mercado Pago' : 'Bancard'}</span>
                      </div>
                    )}
                  </div>
                )}
                <div className="space-y-3">
                  <Link to="/dashboard">
                    <Button className="w-full bg-[#264639] hover:bg-[#1C352B]">
                      Ver mis reservas
                    </Button>
                  </Link>
                  <Link to="/properties">
                    <Button variant="outline" className="w-full border-[#E7E5E4]">
                      <Home className="w-4 h-4 mr-2" /> Explorar más propiedades
                    </Button>
                  </Link>
                </div>
              </>
            )}

            {status === 'expired' && (
              <>
                <div className="w-20 h-20 bg-[#FEE2E2] rounded-full flex items-center justify-center mx-auto mb-6">
                  <XCircle className="w-12 h-12 text-[#991B1B]" />
                </div>
                <h1 className="text-2xl font-bold text-[#1C1917] mb-2" style={{ fontFamily: 'Outfit' }}>
                  Sesión expirada
                </h1>
                <p className="text-[#57534E] mb-6">
                  La sesión de pago ha expirado. Por favor intenta nuevamente.
                </p>
                <Link to="/properties">
                  <Button className="w-full bg-[#264639] hover:bg-[#1C352B]">
                    <ArrowLeft className="w-4 h-4 mr-2" /> Volver a propiedades
                  </Button>
                </Link>
              </>
            )}

            {status === 'error' && (
              <>
                <div className="w-20 h-20 bg-[#FEE2E2] rounded-full flex items-center justify-center mx-auto mb-6">
                  <XCircle className="w-12 h-12 text-[#991B1B]" />
                </div>
                <h1 className="text-2xl font-bold text-[#1C1917] mb-2" style={{ fontFamily: 'Outfit' }}>
                  Error en el pago
                </h1>
                <p className="text-[#57534E] mb-6">
                  Hubo un error al verificar tu pago. Por favor contacta a soporte.
                </p>
                <Link to="/properties">
                  <Button className="w-full bg-[#264639] hover:bg-[#1C352B]">
                    <ArrowLeft className="w-4 h-4 mr-2" /> Volver a propiedades
                  </Button>
                </Link>
              </>
            )}

            {status === 'failed' && (
              <>
                <div className="w-20 h-20 bg-[#FEE2E2] rounded-full flex items-center justify-center mx-auto mb-6">
                  <XCircle className="w-12 h-12 text-[#991B1B]" />
                </div>
                <h1 className="text-2xl font-bold text-[#1C1917] mb-2" style={{ fontFamily: 'Outfit' }}>
                  Pago rechazado
                </h1>
                <p className="text-[#57534E] mb-6">
                  El pago fue rechazado por MercadoPago. Intentá nuevamente con otro medio de pago.
                </p>
                <Link to="/properties">
                  <Button className="w-full bg-[#264639] hover:bg-[#1C352B]">
                    <ArrowLeft className="w-4 h-4 mr-2" /> Volver a propiedades
                  </Button>
                </Link>
              </>
            )}

            {status === 'timeout' && (
              <>
                <div className="w-20 h-20 bg-[#FEF3C7] rounded-full flex items-center justify-center mx-auto mb-6">
                  <Loader2 className="w-12 h-12 text-[#B45309]" />
                </div>
                <h1 className="text-2xl font-bold text-[#1C1917] mb-2" style={{ fontFamily: 'Outfit' }}>
                  Verificación en proceso
                </h1>
                <p className="text-[#57534E] mb-6">
                  Tu pago está siendo procesado. Recibirás un correo con la confirmación.
                </p>
                <Link to="/dashboard">
                  <Button className="w-full bg-[#264639] hover:bg-[#1C352B]">
                    Ir al Dashboard
                  </Button>
                </Link>
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
